import {
    Briefcase,
    Chart,
    EmojiHappy,
    Heart,
    People,
    Profile2User,
    Star1,
    Timer1,
} from "iconsax-react";
import { formatCount, formatNps, formatPercent } from "./helpers";

const GAUGE_COLOR = "#2e71fc";

const DEFAULT_ORDER = [
    "nps",
    "enps",
    "csat",
    "ces",
    "cx",
    "ex",
    "loyalty",
    "engagement",
];

/** Report overview: CSAT & CES first from the right (RTL), then metrics with data. */
export const REPORT_GAUGE_ORDER = [
    "csat",
    "ces",
    "nps",
    "enps",
    "cx",
    "ex",
    "loyalty",
    "engagement",
];

function Gauge({ value, min = 0, max = 100, color = GAUGE_COLOR, display, label }) {
    const numeric = Number(value);
    const hasValue = value != null && Number.isFinite(numeric);
    const span = max - min || 1;
    const pct = hasValue
        ? Math.max(0, Math.min(100, ((numeric - min) / span) * 100))
        : 0;
    return (
        <div
            className="member-report-gauge"
            style={{
                background: `conic-gradient(${color} ${pct}%, #e8eef8 0)`,
            }}
            role="img"
            aria-label={label}
        >
            <span className="tabular-nums">{display}</span>
        </div>
    );
}

function percentDisplay(value, locale) {
    if (value == null || value === "") return "—";
    return `${formatPercent(value, locale)}٪`;
}

function npsColor(score) {
    return Number(score) < 0 ? "#c2413b" : GAUGE_COLOR;
}

function hasGaugeData(item) {
    return item.value != null && Number.isFinite(Number(item.value));
}

export default function CxGauges({
    cx,
    locale,
    t,
    order = DEFAULT_ORDER,
    /** Keys always shown even without a score (e.g. csat, ces on report). */
    alwaysInclude = [],
    /** Drop gauges without a numeric score, unless listed in alwaysInclude. */
    onlyWithData = false,
}) {
    const nps = cx?.nps || {};
    const enps = cx?.enps || {};
    const csat = cx?.csat || {};
    const ces = cx?.ces || {};
    const cxIndex = cx?.cx_index || {};
    const exIndex = cx?.ex_index || {};
    const loyalty = cx?.loyalty || {};
    const engagement = cx?.engagement || {};
    const byKey = {
        nps: {
            key: "nps",
            icon: Chart,
            value: nps.score,
            min: -100,
            max: 100,
            color: npsColor(nps.score),
            display: formatNps(nps.score, locale),
            title: t("Organizational.CustomerClub.Members.Report.Nps"),
            hint: t("Organizational.CustomerClub.Members.Report.Nps.Hint"),
            footnote: t("Organizational.CustomerClub.Members.Report.Sample", {
                count: formatCount(nps.sample, locale),
            }),
        },
        enps: {
            key: "enps",
            icon: Briefcase,
            value: enps.score,
            min: -100,
            max: 100,
            color: npsColor(enps.score),
            display: formatNps(enps.score, locale),
            title: t("Organizational.CustomerClub.Members.Report.Enps"),
            hint: t("Organizational.CustomerClub.Members.Report.Enps.Hint"),
            footnote: t("Organizational.CustomerClub.Members.Report.Sample", {
                count: formatCount(enps.sample, locale),
            }),
        },
        csat: {
            key: "csat",
            icon: EmojiHappy,
            value: csat.score,
            display: percentDisplay(csat.score, locale),
            title: t("Organizational.CustomerClub.Members.Report.Csat"),
            hint: t("Organizational.CustomerClub.Members.Report.Csat.Hint"),
            footnote:
                csat.average == null
                    ? t("Organizational.CustomerClub.Members.Report.Sample", {
                          count: formatCount(csat.sample, locale),
                      })
                    : t("Organizational.CustomerClub.Members.Report.Csat.Average", {
                          value: formatPercent(csat.average, locale),
                      }),
        },
        ces: {
            key: "ces",
            icon: Timer1,
            value: ces.score,
            display: percentDisplay(ces.score, locale),
            title: t("Organizational.CustomerClub.Members.Report.Ces"),
            hint: t("Organizational.CustomerClub.Members.Report.Ces.Hint"),
            footnote:
                ces.average == null
                    ? t("Organizational.CustomerClub.Members.Report.Sample", {
                          count: formatCount(ces.sample, locale),
                      })
                    : t("Organizational.CustomerClub.Members.Report.Csat.Average", {
                          value: formatPercent(ces.average, locale),
                      }),
        },
        cx: {
            key: "cx",
            icon: Star1,
            value: cxIndex.score,
            display: percentDisplay(cxIndex.score, locale),
            title: t("Organizational.CustomerClub.Members.Report.CxIndex"),
            hint: t("Organizational.CustomerClub.Members.Report.CxIndex.Hint"),
            footnote: t("Organizational.CustomerClub.Members.Report.Sample", {
                count: formatCount(cxIndex.sample, locale),
            }),
        },
        ex: {
            key: "ex",
            icon: People,
            value: exIndex.score,
            display: percentDisplay(exIndex.score, locale),
            title: t("Organizational.CustomerClub.Members.Report.ExIndex"),
            hint: t("Organizational.CustomerClub.Members.Report.ExIndex.Hint"),
            footnote: t("Organizational.CustomerClub.Members.Report.Sample", {
                count: formatCount(exIndex.sample, locale),
            }),
        },
        loyalty: {
            key: "loyalty",
            icon: Heart,
            value: loyalty.score,
            display: percentDisplay(loyalty.score, locale),
            title: t("Organizational.CustomerClub.Members.Report.Loyalty"),
            hint: t("Organizational.CustomerClub.Members.Report.Loyalty.Hint"),
            footnote: t(
                "Organizational.CustomerClub.Members.Report.Loyalty.Advocates",
                {
                    count: formatCount(loyalty.advocates, locale),
                    rate: formatPercent(loyalty.advocate_rate, locale),
                }
            ),
        },
        engagement: {
            key: "engagement",
            icon: Profile2User,
            value: engagement.score,
            display: percentDisplay(engagement.score, locale),
            title: t("Organizational.CustomerClub.Members.Report.Engagement"),
            hint: t("Organizational.CustomerClub.Members.Report.Engagement.Hint"),
            footnote: t(
                "Organizational.CustomerClub.Members.Report.Engagement.Active",
                {
                    count: formatCount(engagement.active, locale),
                    total: formatCount(engagement.sample, locale),
                }
            ),
        },
    };

    const always = new Set(alwaysInclude);
    const items = order
        .map((key) => byKey[key])
        .filter(Boolean)
        .filter(
            (item) =>
                !onlyWithData || always.has(item.key) || hasGaugeData(item)
        );

    return (
        <ul className="member-report-gauges">
            {items.map((item) => {
                const Icon = item.icon;
                return (
                    <li key={item.key}>
                        <span
                            className="member-report-metric-icon"
                            aria-hidden="true"
                        >
                            <Icon size={16} />
                        </span>
                        <Gauge
                            value={item.value}
                            min={item.min}
                            max={item.max}
                            color={item.color}
                            display={item.display}
                            label={item.title}
                        />
                        <div className="member-report-gauge-copy">
                            <strong>{item.title}</strong>
                            <span>{item.hint}</span>
                            <em className="tabular-nums">{item.footnote}</em>
                        </div>
                    </li>
                );
            })}
        </ul>
    );
}
